import { EnvironmentValue } from '@plerionhq/types-asset-group';
import { capitalise } from '../../main';
import { useGetEnvironments } from '../../utils/environments';
import { NonProdEnvironmentIcon, ProdEnvironmentIcon } from '../icons';

import { useTranslation } from 'react-i18next';

export type EnvironmentBadgeProps =
  | { id: string }
  | { value: SupportedEnvironments };

export type SupportedEnvironments = EnvironmentValue;

/**
 *
 * Environment badge atom which shows production or non-production environment when provided either
 * an environment id (assetGroupId) OR
 * an environment (i.e. dimension.value when the dimension.key === 'environment')
 *
 * Example:
 * ```tsx
 *   <EnvironmentBadge value="non-production" />
 * ```
 */
export const EnvironmentBadge = (props: EnvironmentBadgeProps) => {
  const { t } = useTranslation('designSystem');
  const { data: environments, isLoading: environmentsLoading } =
    useGetEnvironments();

  if (!environments || environmentsLoading) {
    return <></>;
  }

  const id = 'id' in props ? props.id : undefined;
  const value: SupportedEnvironments | undefined =
    'value' in props ? props.value : undefined;

  const environment = environments.find(
    ({ assetGroupId, dimension }) =>
      assetGroupId === id ||
      (dimension?.key === 'environment' && dimension?.value === value),
  );
  const isProd = environment?.dimension?.value === 'production';

  if (environment) {
    const name = capitalise(environment.name);
    return (
      <span
        aria-label={t('environment', { name })}
        className="bg-badge-info-background inline-flex min-h-6 flex-row items-center rounded-md px-2 py-1 text-xs font-medium whitespace-nowrap"
        data-testid="environment-badge"
        title={t('environment', { name })}
      >
        {isProd ? (
          <ProdEnvironmentIcon className="mr-1 inline-block h-3.5 w-3.5 flex-shrink-0" />
        ) : (
          <NonProdEnvironmentIcon className="mr-1 inline-block h-3.5 w-3.5 flex-shrink-0" />
        )}
        {name}
      </span>
    );
  }
};
